/**
 * BusinessAiDashboardCards.js
 *
 * Compact Happy AI business entry cards for the management dashboard: the
 * latest digest headline with KPI deltas, the open alerts count and the two
 * most advanced active goals. Each card is gated by its own management-access
 * key and the store's AI kill switch; a card whose endpoint fails (e.g. the
 * backend is not deployed yet) simply stays hidden, so the dashboard never
 * shows an error for an optional AI surface.
 *
 * @module components/management/BusinessAiDashboardCards
 */
import React, {useCallback, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {Card, SectionTitle} from './ManagementUI';
import {DeltaChip, GoalBar} from './BusinessAiKit';
import Icon from './SafeIcon';
import {getManagementScope} from '../../helpers/managementApi';
import {dashboardCardKey, readDashboardCard, writeDashboardCard} from '../../helpers/dashboardCardCache';
import {getAlertCount, getDigest, getGoals} from '../../helpers/businessAiApi';
import {formatKpiValue, pickTopGoals} from '../../helpers/businessAi';
import {useManagementAccess} from '../../helpers/managementAccess';
import {useManagementTheme} from '../../helpers/managementTheme';
import useAiAssistantGate from '../../screens/assistant/components/useAiAssistantGate';

/**
 * @returns {?JSX.Element} the cards, or `null` when nothing is accessible / loaded.
 */
export default function BusinessAiDashboardCards() {
    const {t, i18n} = useTranslation();
    const navigation = useNavigation();
    const m = useManagementTheme();
    const {canAccess} = useManagementAccess();
    const aiEnabled = useAiAssistantGate();
    const digestAllowed = aiEnabled && canAccess('businessDigest').allowed;
    const alertsAllowed = aiEnabled && canAccess('businessAlerts').allowed;
    const goalsAllowed = aiEnabled && canAccess('businessGoals').allowed;

    const [digest, setDigest] = useState(null);
    const [openCount, setOpenCount] = useState(null);
    const [goals, setGoals] = useState([]);

    useFocusEffect(useCallback(() => {
        if (!digestAllowed && !alertsAllowed && !goalsAllowed) return undefined;
        let active = true;

        (async () => {
            const scope = await getManagementScope();
            // Fresh payload from a previous focus/mount (2 min TTL): skip the three aggregate calls.
            const cacheKey = dashboardCardKey('bizai', scope, i18n.language, digestAllowed, alertsAllowed, goalsAllowed);
            const cached = readDashboardCard(cacheKey);

            if (cached) {
                if (!active) return;
                setDigest(cached.digest);
                setOpenCount(cached.openCount);
                setGoals(cached.goals);

                return;
            }

            // Independent calls, each already non-throwing ({ok}); Promise.all is Hermes-safe.
            const [digestRes, countRes, goalsRes] = await Promise.all([
                digestAllowed ? getDigest({period: 'day', locale: i18n.language}, scope) : null,
                alertsAllowed ? getAlertCount(scope) : null,
                goalsAllowed ? getGoals({status: 'active'}, scope) : null,
            ]);

            if (!active) return;
            const nextDigest = digestRes?.ok ? digestRes.data : null;
            const nextOpenCount = countRes?.ok && Number.isFinite(Number(countRes.data?.openCount)) ? Number(countRes.data.openCount) : null;
            const nextGoals = goalsRes?.ok ? pickTopGoals(goalsRes.data?.items, 2) : [];

            setDigest(nextDigest);
            setOpenCount(nextOpenCount);
            setGoals(nextGoals);
            if (digestRes?.ok || countRes?.ok || goalsRes?.ok) {
                writeDashboardCard(cacheKey, {digest: nextDigest, openCount: nextOpenCount, goals: nextGoals});
            }
        })();

        return () => { active = false; };
    }, [digestAllowed, alertsAllowed, goalsAllowed, i18n.language]));

    const headline = digest?.narrative?.headline;
    const kpis = Array.isArray(digest?.kpis) ? digest.kpis.slice(0, 3) : [];
    const showDigest = digestAllowed && !!digest;
    const showAlerts = alertsAllowed && openCount !== null;
    const showGoals = goalsAllowed && goals.length > 0;

    if (!showDigest && !showAlerts && !showGoals) return null;

    return (
        <>
            <SectionTitle title={t('bizai.digest_title')} icon="creation" eyebrow />
            {showDigest && (
                <TouchableOpacity activeOpacity={0.85} onPress={() => navigation.navigate('BusinessDigestScreen')}>
                    <Card style={styles.card}>
                        <Text style={[styles.headline, {color: m.colors.heading}]} numberOfLines={3}>{headline || t('bizai.dash_digest_empty')}</Text>
                        {kpis.map((kpi) => (
                            <View key={kpi.key} style={styles.kpiRow}>
                                <Text style={[styles.kpiLabel, {color: m.colors.subText}]} numberOfLines={1}>{kpi.label}</Text>
                                <Text style={[styles.kpiValue, {color: m.colors.heading}]} numberOfLines={1}>{formatKpiValue(kpi.value, kpi.unit)}</Text>
                                <DeltaChip deltaPct={kpi.deltaPct} />
                            </View>
                        ))}
                    </Card>
                </TouchableOpacity>
            )}
            {showAlerts && (
                <TouchableOpacity activeOpacity={0.85} onPress={() => navigation.navigate('BusinessAlertsScreen')}>
                    <Card style={[styles.card, styles.row]}>
                        <Icon name={openCount > 0 ? 'bell-alert' : 'bell-check-outline'} size={22} color={openCount > 0 ? '#F97316' : m.toneColor('success')} />
                        <View style={styles.flex}>
                            <Text style={[styles.headline, {color: m.colors.heading}]}>{t('bizai.dash_alerts_title')}</Text>
                            <Text style={[styles.kpiLabel, {color: m.colors.subText}]}>
                                {openCount > 0 ? t('bizai.dash_alerts_open', {count: openCount}) : t('bizai.dash_alerts_none')}
                            </Text>
                        </View>
                        <Icon name="chevron-right" size={20} color={m.colors.subText} />
                    </Card>
                </TouchableOpacity>
            )}
            {showGoals && (
                <TouchableOpacity activeOpacity={0.85} onPress={() => navigation.navigate('BusinessGoalsScreen')}>
                    <Card style={styles.card}>
                        <Text style={[styles.headline, {color: m.colors.heading}]}>{t('bizai.dash_goals_title')}</Text>
                        {goals.map((goal) => {
                            const p = goal.progress || goal;

                            return (
                                <View key={goal._id} style={styles.goal}>
                                    <View style={styles.kpiRow}>
                                        <Text style={[styles.kpiLabel, styles.flex, {color: m.colors.subText}]} numberOfLines={1}>{goal.title}</Text>
                                        <Text style={[styles.kpiValue, {color: m.colors.heading}]}>{`${Math.round(Number(p.pct) || 0)}%`}</Text>
                                    </View>
                                    <GoalBar pct={Number(p.pct) || 0} projectedPct={p.projectedPct} onTrack={typeof p.onTrack === 'boolean' ? p.onTrack : undefined} />
                                </View>
                            );
                        })}
                    </Card>
                </TouchableOpacity>
            )}
        </>
    );
}

const styles = StyleSheet.create({
    card: {marginBottom: 12},
    row: {flexDirection: 'row', alignItems: 'center', gap: 12},
    flex: {flex: 1},
    headline: {fontSize: 15, fontWeight: '700', lineHeight: 21},
    kpiRow: {flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8},
    kpiLabel: {flexShrink: 1, fontSize: 12.5, fontWeight: '600'},
    kpiValue: {fontSize: 13.5, fontWeight: '800', marginLeft: 'auto'},
    goal: {marginTop: 10},
});
